/**
 * Age Calculator JavaScript
 * Handles age calculation functionality
 */
class AgeCalculator {
    // Constants
    static get DATE_FORMAT() {
        return {
            PLACEHOLDER: 'MM-DD-YYYY',
            PATTERN: /^(\d{2})-(\d{2})-(\d{4})$/,
            ISO_PATTERN: /^(\d{4})-(\d{2})-(\d{2})$/,
            SLASH_PATTERN: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
            DIGITS_LENGTH: 8,
            MAX_MONTH: 12,
            MIN_MONTH: 1,
            MAX_DAY: 31,
            MIN_DAY: 1,
            MIN_YEAR: 1900,
            MAX_YEAR: 2100,
        };
    }

    static get SELECTORS() {
        return {
            BIRTH_DATE: 'birth-date',
            AGE_ON_DATE: 'age-on-date',
            CALCULATE_BUTTON: 'calculate-age',
            CLEAR_BUTTON: 'clear-form',
            RESULTS_CONTAINER: 'age-results',
            YEARS_RESULT: 'years-result',
            MONTHS_RESULT: 'months-result',
            WEEKS_RESULT: 'weeks-result',
            DAYS_RESULT: 'days-result',
            FORM: '.age-calculator__form',
            ERROR: '.age-calculator__error',
        };
    }

    constructor() {
        this.elements = this.initializeElements();
        this.lastValidationError = null;
        
        if (!this.elements) {
            console.warn('Age calculator elements not found');
            return;
        }
        
        this.init();
    }

    initializeElements() {
        const birthDateInput = document.getElementById(AgeCalculator.SELECTORS.BIRTH_DATE);
        const ageOnDateInput = document.getElementById(AgeCalculator.SELECTORS.AGE_ON_DATE);
        const calculateButton = document.getElementById(AgeCalculator.SELECTORS.CALCULATE_BUTTON);
        const clearButton = document.getElementById(AgeCalculator.SELECTORS.CLEAR_BUTTON);
        const resultsContainer = document.getElementById(AgeCalculator.SELECTORS.RESULTS_CONTAINER);

        if (!birthDateInput || !ageOnDateInput || !calculateButton || !clearButton || !resultsContainer) {
            return null;
        }

        return {
            birthDateInput,
            ageOnDateInput,
            calculateButton,
            clearButton,
            resultsContainer,
            yearsResult: document.getElementById(AgeCalculator.SELECTORS.YEARS_RESULT),
            monthsResult: document.getElementById(AgeCalculator.SELECTORS.MONTHS_RESULT),
            weeksResult: document.getElementById(AgeCalculator.SELECTORS.WEEKS_RESULT),
            daysResult: document.getElementById(AgeCalculator.SELECTORS.DAYS_RESULT),
        };
    }

    init() {
        this.attachEventListeners();
    }

    attachEventListeners() {
        const { calculateButton, clearButton, birthDateInput, ageOnDateInput } = this.elements;

        // Button events
        calculateButton.addEventListener('click', () => this.calculateAge());
        clearButton.addEventListener('click', () => this.clearForm());

        // Date input events - both inputs share the same handlers
        const dateInputs = [birthDateInput, ageOnDateInput];
        dateInputs.forEach(input => {
            input.addEventListener('change', () => this.handleDateChange());
            input.addEventListener('input', (e) => this.formatDateInput(e));
            input.addEventListener('paste', (e) => this.handlePaste(e));
        });
    }

    handleDateChange() {
        this.clearResults();
        this.validateInputs();
    }

    formatDateToMMDDYYYY(date) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
    }

    formatDigitsAsDate(digits) {
        if (digits.length > AgeCalculator.DATE_FORMAT.DIGITS_LENGTH) {
            digits = digits.substring(0, AgeCalculator.DATE_FORMAT.DIGITS_LENGTH);
        }

        let formatted = '';
        if (digits.length > 0) {
            formatted = digits.substring(0, 2);
            if (digits.length > 2) {
                formatted += '-' + digits.substring(2, 4);
            }
            if (digits.length > 4) {
                formatted += '-' + digits.substring(4, 8);
            }
        }
        return formatted;
    }

    formatDateInput(e) {
        const digits = e.target.value.replace(/\D/g, '');
        e.target.value = this.formatDigitsAsDate(digits);
    }

    handlePaste(e) {
        e.preventDefault();
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
        
        // Try to parse various date formats
        const date = this.parseDate(pastedText);
        if (date && !isNaN(date.getTime())) {
            e.target.value = this.formatDateToMMDDYYYY(date);
        } else {
            // If parsing fails, format as digits
            const digits = pastedText.replace(/\D/g, '');
            e.target.value = this.formatDigitsAsDate(digits);
        }
    }

    validateDateComponents(day, month, year) {
        const { MIN_MONTH, MAX_MONTH, MIN_DAY, MAX_DAY, MIN_YEAR, MAX_YEAR } = AgeCalculator.DATE_FORMAT;

        if (month < MIN_MONTH || month > MAX_MONTH) {
            return { valid: false, error: 'Month must be between 01 and 12' };
        }
        
        if (day < MIN_DAY || day > MAX_DAY) {
            return { valid: false, error: 'Day must be between 01 and 31' };
        }
        
        if (year < MIN_YEAR || year > MAX_YEAR) {
            return { valid: false, error: 'Year must be between 1900 and 2100' };
        }
        
        // Create date and verify it matches the input (catches invalid dates like Feb 30)
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() !== year || 
            date.getMonth() !== month - 1 || 
            date.getDate() !== day) {
            return { valid: false, error: 'Invalid date (e.g., day does not exist in this month)' };
        }
        
        return { valid: true };
    }

    parseDateWithValidation(month, day, year) {
        const validation = this.validateDateComponents(day, month, year);
        if (!validation.valid) {
            this.lastValidationError = validation.error;
            return null;
        }
        return new Date(year, month - 1, day);
    }

    parseDate(dateString) {
        const { PATTERN, ISO_PATTERN, SLASH_PATTERN } = AgeCalculator.DATE_FORMAT;

        // Try to parse mm-dd-yyyy format
        const mmddyyyyMatch = dateString.match(PATTERN);
        if (mmddyyyyMatch) {
            const month = parseInt(mmddyyyyMatch[1], 10);
            const day = parseInt(mmddyyyyMatch[2], 10);
            const year = parseInt(mmddyyyyMatch[3], 10);
            return this.parseDateWithValidation(month, day, year);
        }
        
        // Try to parse yyyy-mm-dd format (ISO)
        const yyyymmddMatch = dateString.match(ISO_PATTERN);
        if (yyyymmddMatch) {
            const year = parseInt(yyyymmddMatch[1], 10);
            const month = parseInt(yyyymmddMatch[2], 10);
            const day = parseInt(yyyymmddMatch[3], 10);
            return this.parseDateWithValidation(month, day, year);
        }
        
        // Try to parse mm/dd/yyyy or dd/mm/yyyy
        const slashMatch = dateString.match(SLASH_PATTERN);
        if (slashMatch) {
            const part1 = parseInt(slashMatch[1], 10);
            const part2 = parseInt(slashMatch[2], 10);
            const year = parseInt(slashMatch[3], 10);
            
            let day, month;
            // If part1 > 12, it must be day (dd/mm/yyyy format)
            if (part1 > AgeCalculator.DATE_FORMAT.MAX_MONTH) {
                day = part1;
                month = part2;
            }
            // If part2 > 12, it must be mm/dd/yyyy
            else if (part2 > AgeCalculator.DATE_FORMAT.MAX_MONTH) {
                day = part2;
                month = part1;
            }
            // Ambiguous - assume mm/dd/yyyy (MM-DD-YYYY format)
            else {
                month = part1;
                day = part2;
            }
            
            return this.parseDateWithValidation(month, day, year);
        }
        
        // Try to parse 8 digits as mmddyyyy
        const digits = dateString.replace(/\D/g, '');
        if (digits.length === AgeCalculator.DATE_FORMAT.DIGITS_LENGTH) {
            const month = parseInt(digits.substring(0, 2), 10);
            const day = parseInt(digits.substring(2, 4), 10);
            const year = parseInt(digits.substring(4, 8), 10);
            return this.parseDateWithValidation(month, day, year);
        }
        
        return null;
    }

    validateInputs() {
        const { birthDateInput, ageOnDateInput } = this.elements;
        const birthDate = birthDateInput.value;
        const ageOnDate = ageOnDateInput.value;
        
        this.lastValidationError = null;
        
        if (!birthDate || !ageOnDate) {
            return false;
        }

        const birth = this.parseDate(birthDate);
        if (!birth || isNaN(birth.getTime())) {
            const errorMsg = this.lastValidationError || `Please enter a valid birth date in ${AgeCalculator.DATE_FORMAT.PLACEHOLDER} format`;
            this.showError(errorMsg);
            return false;
        }
        
        this.lastValidationError = null;
        
        const ageOn = this.parseDate(ageOnDate);
        if (!ageOn || isNaN(ageOn.getTime())) {
            const errorMsg = this.lastValidationError || `Please enter a valid date in ${AgeCalculator.DATE_FORMAT.PLACEHOLDER} format`;
            this.showError(errorMsg);
            return false;
        }
            
            // Check if birth date is not in the future
            if (birth > new Date()) {
                this.showError('Birth date cannot be in the future');
                return false;
            }
            
            // Check if age on date is not before birth date
            if (ageOn < birth) {
                this.showError('Age calculation date cannot be before birth date');
                return false;
            }
            
            this.clearError();
            return true;
    }

    calculateAge() {
        if (!this.validateInputs()) {
            return;
        }

        const { birthDateInput, ageOnDateInput } = this.elements;
        const birthDate = this.parseDate(birthDateInput.value);
        const ageOnDate = this.parseDate(ageOnDateInput.value);
        
        const ageData = this.calculateAgeDifference(birthDate, ageOnDate);
        this.displayResults(ageData);
        this.elements.calculateButton.classList.add('active');
    }

    calculateAgeDifference(birthDate, ageOnDate) {
        const diffTime = ageOnDate - birthDate;
        const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const weeks = Math.floor(days / 7);
        const months = this.calculateMonths(birthDate, ageOnDate);
        const years = Math.floor(months / 12);
        
        return { years, months, weeks, days };
    }

    calculateMonths(birthDate, ageOnDate) {
        let months = (ageOnDate.getFullYear() - birthDate.getFullYear()) * 12;
        months += ageOnDate.getMonth() - birthDate.getMonth();
        
        // Adjust if the day hasn't occurred yet this month
        if (ageOnDate.getDate() < birthDate.getDate()) {
            months--;
        }
        
        return months;
    }

    displayResults(ageData) {
        const { yearsResult, monthsResult, weeksResult, daysResult, resultsContainer } = this.elements;
        
        yearsResult.textContent = this.formatNumber(ageData.years);
        monthsResult.textContent = this.formatNumber(ageData.months);
        weeksResult.textContent = this.formatNumber(ageData.weeks);
        daysResult.textContent = this.formatNumber(ageData.days);
        
        resultsContainer.style.display = 'grid';
        
        resultsContainer.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
        });
    }

    formatNumber(num) {
        return num.toLocaleString();
    }

    clearResults() {
        this.elements.resultsContainer.style.display = 'none';
        this.elements.calculateButton.classList.remove('active');
    }

    clearForm() {
        const { birthDateInput, ageOnDateInput } = this.elements;
        birthDateInput.value = '';
        ageOnDateInput.value = '';
        this.clearResults();
        this.clearError();
    }

    showError(message) {
        this.clearError();
        
        const errorDiv = document.createElement('div');
        errorDiv.className = 'age-calculator__error';
        errorDiv.textContent = message;
        
        const form = document.querySelector(AgeCalculator.SELECTORS.FORM);
        if (form) {
        form.insertAdjacentElement('afterend', errorDiv);
        }
    }

    clearError() {
        const existingError = document.querySelector(AgeCalculator.SELECTORS.ERROR);
        if (existingError) {
            existingError.remove();
        }
    }
}

// Initialize the age calculator when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    if (document.getElementById(AgeCalculator.SELECTORS.BIRTH_DATE)) {
        new AgeCalculator();
    }
});
